import { useEffect, useState, useMemo } from 'react'
import { Form, Button } from 'antd'
import { SvgIcon } from '@/components'
import styled from 'styled-components'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import {
  useEmailCreateTask,
  useGetAgentList,
  useGetSecurityDevice,
  useGetEmailSendList,
} from './utils'
import { useGetTaskBaseInfo } from '@/pages/waf/task-list/utils'
import { Message } from '@/components/message'
import { useSelections } from 'ahooks'
import EmptyImg from '@/assets/imges/Empty/Empty.png'
import { EmailCreateConfig } from './email-create-config'
import { EmailCreateTemplate } from './email-create-template'
import moment from 'moment'
import { LocationState, TemplateItem } from './interface'
import { t } from '@/i18n'

const { useForm } = Form

export const EmailCreateTask = () => {
  const [createForm] = useForm()
  const navigate = useNavigate()
  const { state: stateParams } = useLocation() as { state: LocationState }
  const [search, setSearch] = useState({})
  const [getAgentList] = useGetAgentList()
  const [emailSendList] = useGetEmailSendList()
  const [getSecurityDevice] = useGetSecurityDevice()
  const [createTask, createTaskLoading] = useEmailCreateTask()
  const [getTaskBaseInfo] = useGetTaskBaseInfo()
  const [step, setStep] = useState(1)
  const [areaData, setAreaData] = useState([])
  // 存储表单数据
  const [formData, setFormData] = useState<any>({})
  // 存储选中的caseIds
  const [selectedCaseIds, setSelectedCaseIds] = useState<string[]>([])
  // 存储选中的模板IDs
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateItem[]>([])
  // 存储执行模板最新用例的状态
  const [executeTemplateLatestCase, setExecuteTemplateLatestCase] = useState(false)
  // 存储历史数据
  const [historyData, setHistoryData] = useState<any>(null)
  // 存储模板和用例的状态
  const [templateState, setTemplateState] = useState({
    selectedTemplateIds: [] as string[],
    selectedCaseIds: [] as string[],
    executeTemplateLatestCase: false,
    selectedTemplateId: '', // 添加选中的模板ID
    templateSwitchStates: {} as { [key: string]: boolean }, // 添加存储每个模板Switch状态的字段
  })
  // 获取历史数据
  useEffect(() => {
    console.log('stateParams-----', stateParams)
    const fetchTaskData = async () => {
      if (stateParams?.id && areaData.length > 0) {
        try {
          const res = await getTaskBaseInfo(stateParams.id)
          setHistoryData(res.data)

          // 处理安全设备数据
          let securityDeviceIds: any = []
          if (res.data.deviceIds?.length > 0) {
            try {
              const deviceRes = await getSecurityDevice({ emailId: res.data.siteId })
              console.log('deviceRes', deviceRes)
              const deviceList = deviceRes.data.siteDeviceList || []

              // 遍历设备列表
              for (const item of deviceList) {
                if (!item.devices?.length) continue

                // 遍历每个设备
                for (const child of item.devices) {
                  // 检查是否在 deviceIds 中
                  if (res.data.deviceIds.includes(child.deviceId)) {
                    securityDeviceIds.push([item.deviceType, child.deviceId])
                  }
                }
              }

              console.log('securityDeviceIds', securityDeviceIds)

              // 处理时间值
              let taskTime
              if (
                res.data.taskKind === 'scheduler_type_daily' ||
                res.data.taskKind === 'scheduler_type_weekly' ||
                res.data.taskKind === 'scheduler_type_monthly'
              ) {
                taskTime = res.data.cronSchedule?.timeOfDay
              } else {
                taskTime = res.data.cycleTime
              }

              // 设置表单数据
              const newFormData = {
                name: res.data.name,
                business_site_id: res.data.siteId,
                group_id: res.data.groupId,
                execute_template_latest_case: res.data.execute_template_latest_case,
                task_time: taskTime ? moment(taskTime, 'HH:mm:ss') : undefined,
                reduce_speed: res.data.reduceSpeed,
                priority: res.data.priority,
                device_id: securityDeviceIds,
                execute_type:
                  res.data.taskKind === 'scheduler_type_minutely' ||
                  res.data.taskKind === 'scheduler_type_hourly'
                    ? 4
                    : res.data.taskKind === 'scheduler_type_immediate'
                    ? 1
                    : res.data.taskKind === 'scheduler_type_reserve'
                    ? 2
                    : 3,
                execute_interval:
                  res.data.cronSchedule?.intervalOfMinute ||
                  res.data.cronSchedule?.intervalOfHour ||
                  1,
                execute_unit: res.data.cronSchedule?.intervalOfMinute ? 'minute' : 'hour',
                scheduler_type: res.data.taskKind,
                start_time: res.data.cycleTime ? moment(res.data.cycleTime) : undefined,
                execution_times: res.data.executeTimesLimit || 0,
                reportFilter: res.data.reportFilter || false,
                send_filter: res.data.sendFilter || '',
                report_email: res.data.report_email || false,
                delay: res.data.delay || 130,
                interval: res.data.interval || 1,
              }

              console.log('Setting form data with task_time:', taskTime)
              setFormData(newFormData)
              createForm.setFieldsValue(newFormData)
            } catch (error) {
              console.error('获取安全设备数据失败:', error)
              // 即使获取设备数据失败，也要设置其他表单数据
              const newFormData = {
                name: res.data.name,
                business_site_id: res.data.siteId,
                group_id: res.data.groupId,
                execute_template_latest_case: res.data.execute_template_latest_case,
                task_time: res.data.cycleTime ? moment(res.data.cycleTime) : undefined,
                reduce_speed: res.data.reduceSpeed,
                priority: res.data.priority,
                device_id: '', // 设备数据获取失败时设置为空数组
                execute_type:
                  res.data.taskKind === 'scheduler_type_minutely' ||
                  res.data.taskKind === 'scheduler_type_hourly'
                    ? 4
                    : res.data.taskKind === 'scheduler_type_immediate'
                    ? 1
                    : res.data.taskKind === 'scheduler_type_reserve'
                    ? 2
                    : 3,
                execute_interval:
                  res.data.cronSchedule?.intervalOfMinute ||
                  res.data.cronSchedule?.intervalOfHour ||
                  1,
                execute_unit: res.data.cronSchedule?.intervalOfMinute ? 'minute' : 'hour',
                scheduler_type: res.data.taskKind,
                start_time: res.data.cycleTime ? moment(res.data.cycleTime) : undefined,
                execution_times: res.data.executeTimesLimit || 0,
                reportFilter: res.data.reportFilter || false,
                send_filter: res.data.sendFilter || '',
                report_email: res.data.report_email || false,
                delay: res.data.delay || 130,
                interval: res.data.interval || 1,
              }

              setFormData(newFormData)
              createForm.setFieldsValue(newFormData)
            }
          } else {
            // 没有设备数据时，直接设置其他表单数据
            const newFormData = {
              name: res.data.name,
              business_site_id: res.data.siteId,
              group_id: res.data.groupId,
              execute_template_latest_case: res.data.execute_template_latest_case,
              task_time: res.data.cycleTime ? moment(res.data.cycleTime) : undefined,
              reduce_speed: res.data.reduceSpeed,
              priority: res.data.priority,
              device_id: '', // 没有设备数据时设置为空数组
              execute_type:
                res.data.taskKind === 'scheduler_type_minutely' ||
                res.data.taskKind === 'scheduler_type_hourly'
                  ? 4
                  : res.data.taskKind === 'scheduler_type_immediate'
                  ? 1
                  : res.data.taskKind === 'scheduler_type_reserve'
                  ? 2
                  : 3,
              execute_interval:
                res.data.cronSchedule?.intervalOfMinute ||
                res.data.cronSchedule?.intervalOfHour ||
                1,
              execute_unit: res.data.cronSchedule?.intervalOfMinute ? 'minute' : 'hour',
              scheduler_type: res.data.taskKind,
              start_time: res.data.cycleTime ? moment(res.data.cycleTime) : undefined,
              execution_times: res.data.executeTimesLimit || 0,
              reportFilter: res.data.reportFilter || false,
              send_filter: res.data.sendFilter || '',
              report_email: res.data.report_email || false,
              delay: res.data.delay || 130,
              interval: res.data.interval || 1,
            }

            setFormData(newFormData)
            createForm.setFieldsValue(newFormData)
          }
        } catch (error) {
          console.error('获取任务数据失败:', error)
        }
      }
    }

    fetchTaskData()
  }, [stateParams?.id, areaData.length])

  // 处理模板和用例状态变化
  const handleTemplateStateChange = (newState: {
    selectedTemplateIds?: string[]
    selectedCaseIds?: string[]
    executeTemplateLatestCase?: boolean
    selectedTemplateId?: string
    templateSwitchStates?: { [key: string]: boolean }
  }) => {
    console.log('handleTemplateStateChange called with:', newState)
    setTemplateState(prev => {
      const updatedState = {
        ...prev,
        ...newState,
      }
      console.log('Updated templateState:', updatedState)
      return updatedState
    })

    // 如果 selectedCaseIds 发生变化，同时更新 selectedCaseIds 状态
    if (newState.selectedCaseIds) {
      console.log('Updating selectedCaseIds:', newState.selectedCaseIds)
      setSelectedCaseIds(newState.selectedCaseIds)
    }

    // 如果 selectedTemplateIds 发生变化，需要同步更新 selectedTemplate
    if (newState.selectedTemplateIds) {
      console.log('Updating selectedTemplateIds:', newState.selectedTemplateIds)
      // 这里需要从模板组件获取完整的模板数据，暂时保持现有逻辑
      // selectedTemplate 会在模板组件的 onTemplateIdsChange 回调中更新
    }

    // 如果 executeTemplateLatestCase 发生变化，同时更新 formData
    if (newState.executeTemplateLatestCase !== undefined) {
      setFormData(prev => ({
        ...prev,
        execute_template_latest_case: newState.executeTemplateLatestCase,
      }))
      setExecuteTemplateLatestCase(newState.executeTemplateLatestCase)
    }
  }

  // 返回上一步时，清空模板和用例选择数据，保留第一步表单信息
  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1)
      // 清空所有选中的模板和用例数据
      setSelectedTemplate([])
      setSelectedCaseIds([])
      // 重置模板状态为初始值
      setTemplateState({
        selectedTemplateIds: [],
        selectedCaseIds: [],
        executeTemplateLatestCase: false,
        selectedTemplateId: '',
        templateSwitchStates: {},
      })
      console.log('清空模板和用例选择，保留第一步表单信息')
    }
  }

  // 提交表单
  const submitForm = async values => {
    console.log('values1111', values)
    // 更新表单数据，保留用户修改后的值
    setFormData(prev => ({
      ...prev,
      ...values,
    }))
    // 只有在周期任务时才设置 executeTemplateLatestCase
    if (values.execute_type === 3) {
      setExecuteTemplateLatestCase(values.execute_template_latest_case)
      setTemplateState(prev => ({
        ...prev,
        executeTemplateLatestCase: values.execute_template_latest_case,
      }))
    } else {
      setExecuteTemplateLatestCase(false)
      setTemplateState(prev => ({
        ...prev,
        executeTemplateLatestCase: false,
      }))
    }
    setStep(2)
  }

  // 提交表单
  const submitHandler = async () => {
    try {
      // 获取表单数据
      const values = await createForm.validateFields()
      console.log('selectedTemplate', selectedTemplate)
      console.log('selectedCaseIds', selectedCaseIds)
      console.log('表单数据:', values)

      if (step === 1) {
        // 如果验证通过，进入下一步
        submitForm(values)
      } else {
        try {
          // 合并第一步和第二步的表单数据
          const finalData = {
            ...formData,
            ...values,
            case_ids: selectedCaseIds, // 添加选中的caseIds
            template_ids: selectedTemplate.map(item => item.templateId), // 添加选中的模板IDs
            execute_template_latest_case: executeTemplateLatestCase, // 确保这个值被正确传递
          }

          const templateIds = selectedTemplate.map(item => ({
            id: item.templateId,
            useLatestResource: item.executeUpdate,
          }))

          let schedulerType = ''
          const cronSchedule = {
            dayOfWeek: undefined,
            dayOfMonth: undefined,
            timeOfDay: '',
            intervalOfHour: undefined,
            intervalOfMinute: undefined,
          }
          let startTime = ''

          if (finalData.execute_type === 1) {
            schedulerType = 'scheduler_type_immediate'
          }
          if (finalData.execute_type === 2) {
            schedulerType = 'scheduler_type_reserve'
            startTime = moment(finalData?.task_time).format('YYYY-MM-DD HH:mm:ss')
          }

          if (finalData.execute_unit === 'hour') {
            schedulerType = 'scheduler_type_hourly'
            cronSchedule.intervalOfHour = finalData.execute_interval
            startTime = moment(finalData?.task_time).format('YYYY-MM-DD HH:mm:ss')
          }

          if (finalData.execute_unit === 'minute') {
            schedulerType = 'scheduler_type_minutely'
            cronSchedule.intervalOfMinute = finalData.execute_interval
            startTime = moment(finalData?.task_time).format('YYYY-MM-DD HH:mm:ss')
          }

          if (finalData.scheduler_type === 'scheduler_type_monthly') {
            schedulerType = 'scheduler_type_monthly'
            cronSchedule.dayOfMonth = finalData.day_of_month
            cronSchedule.timeOfDay = finalData?.task_time
              ? moment(finalData?.task_time).format('HH:mm:ss')
              : ''
            startTime = ''
          }

          if (finalData.scheduler_type === 'scheduler_type_weekly') {
            schedulerType = 'scheduler_type_weekly'
            cronSchedule.dayOfWeek = finalData.day_of_week
            cronSchedule.timeOfDay = finalData?.task_time
              ? moment(finalData?.task_time).format('HH:mm:ss')
              : ''
            startTime = ''
          }

          if (finalData.scheduler_type === 'scheduler_type_daily') {
            schedulerType = 'scheduler_type_daily'
            cronSchedule.timeOfDay = finalData?.task_time
              ? moment(finalData?.task_time).format('HH:mm:ss')
              : ''
            startTime = ''
          }

          console.log('cronSchedule', cronSchedule)

          const params = {
            name: finalData.name,
            group_id: finalData.group_id,
            priority: finalData.priority,
            offlineVerify: false,
            verification_mode_id: 'VerificationModePlatToEmail',
            email_server_id: finalData.email_server_id,
            target_email_id: finalData.target_email_id[2],
            agent_id: finalData.agent_id || '0',
            device_id: finalData.device_id?.length > 0 ? finalData.device_id[1] : '0',
            templates: templateIds,
            resource_ids: selectedCaseIds,
            task_time: startTime,
            scheduler_type: schedulerType,
            cron_schedule: cronSchedule,
            interval: finalData.interval,
            delay: finalData.delay,
            report_email: finalData.report_email || false,
            reportFilter: finalData.reportFilter || false,
            send_filter: finalData.send_filter ? [finalData.send_filter] : ['ConditionAnyway'],
            executionTimes: finalData.execution_times,
            report_email_list: finalData?.emailList,
          }
          console.log('params', params)
          await createTask(params as any)
          Message.success(t('任务创建成功'))
          setTimeout(() => {
            navigate('/validation/network/emailgateway?tab=task')
          }, 100)
        } catch (error) {
          console.error(error)
        }
      }
    } catch (error: any) {
      console.error('表单验证失败:', error)
      const values = error.values
      if (values.scheduler_type && !values.task_time) {
        Message.error(t('请输入时间'))
      } else if (values.execute_type === 4 && !values.execute_interval) {
        Message.error(t('请输入时间间隔'))
      }
    }
  }

  return (
    <Container>
      <div className="title">{t('创建任务')}</div>
      {/* 步骤指示器 */}
      <div className="steps-container">
        <div className="steps">
          <div className="step-number active number-1">01</div>
          <div className={step === 1 ? 'step-number number-2' : 'step-number number-2 active'}>
            02
          </div>
          <div className="step-wrapper-container">
            <div className="step-wrapper">
              <div className="step-content active">
                <div className="step-icon">
                  <SvgIcon width="40" height="40" name={'任务配置'} />
                </div>
                <span className="step-text">{t('任务配置')}</span>
              </div>
            </div>
            <div className="step-arrow">
              <SvgIcon
                width="24"
                height="28"
                name={step === 1 ? 'step-arrow-normal' : 'step-arrow-active'}
              />
            </div>
            <div className="step-wrapper">
              <div className="step-content">
                <div className="step-icon">
                  <SvgIcon
                    width="40"
                    height="40"
                    name={step === 1 ? '模版及用例选择灰' : '模版及用例选择'}
                  />
                </div>
                <span className="step-text" style={{ color: step === 1 ? '#999' : '#333' }}>
                  {t('模板及用例选择')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 任务配置 */}
      {step === 1 && (
        <EmailCreateConfig
          submitForm={submitForm}
          createForm={createForm}
          areaData={areaData}
          initialValues={{
            ...formData,
            execute_template_latest_case:
              formData.execute_type === 3 ? executeTemplateLatestCase : false,
          }}
        />
      )}

      {/* 模板及用例选择 */}
      {step === 2 && (
        <EmailCreateTemplate
          onCaseIdsChange={setSelectedCaseIds}
          onTemplateIdsChange={setSelectedTemplate}
          executeTemplateLatestCase={
            formData.execute_type === 3 ? executeTemplateLatestCase : false
          }
          templateState={templateState}
          onTemplateStateChange={handleTemplateStateChange}
          executeType={formData.execute_type}
        />
      )}

      {/* 底部按钮 */}
      <div className="footer">
        {step === 2 && (
          <Button type="default" onClick={handleBack}>
            {t('上一步')}
          </Button>
        )}
        <Button
          type="primary"
          onClick={submitHandler}
          disabled={step === 2 && selectedCaseIds.length === 0}
          loading={createTaskLoading}
        >
          {step === 1 ? t('下一步') : t('创建任务')}
        </Button>
      </div>
    </Container>
  )
}

const Container = styled.div`
  /* padding: 0 16px; */
  background: #fff;
  border-radius: 8px;
  .title {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin-bottom: 30px;
    margin-top: 14px;
    margin-left: 16px;
  }

  // 标题下划线样式
  .ant-sign-title {
    position: relative;
    padding-bottom: 12px;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100px;
      height: 2px;
      background: linear-gradient(90deg, #2b5adc 0%, #22b9e7 100%);
    }
  }

  .container {
    height: calc(100% - 208px);
  }
  .loading-container {
    height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .template-list {
    background: #f7f8f9;
    border-radius: 8px;
    padding: 16px;
  }
  .template-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
  }
  .template-item {
    background: white;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    align-items: center;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.3s;

    &:hover {
      border-color: #1890ff;
    }

    &.selected {
      border-color: #1890ff;
      background: #e6f7ff;
    }
  }
  .template-content {
    flex: 1;
    display: flex;
    align-items: center;
  }
  .template-icon {
    margin-right: 16px;
  }
  .template-info {
    flex: 1;
  }
  .template-name {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin-bottom: 4px;
  }
  .template-desc {
    font-size: 12px;
    color: #666;
  }
  .template-detail {
    margin-left: 16px;
    color: #1890ff;
    font-size: 16px;
    cursor: pointer;

    &:hover {
      color: #40a9ff;
    }
  }
  .template-detail-content {
    padding: 16px;

    .detail-item {
      margin-bottom: 16px;
      display: flex;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .detail-label {
      width: 100px;
      color: #666;
      flex-shrink: 0;
    }

    .detail-value {
      color: #333;
    }
  }

  .verify-config {
    background: #f7f8f9;
    border-radius: 8px;
    padding: 16px;
    margin-top: 16px;
    margin-left: 120px;

    .config-title {
      color: #666;
      margin-bottom: 16px;
    }

    .config-form {
      .ant-form-item {
        margin-bottom: 24px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .flex-row {
    display: flex;
    align-items: center;
    gap: 6px;

    .label {
      width: 100px;
      color: #666;
    }
  }

  .device-list {
    margin-top: 16px;
    padding: 16px;
    background: #fff;
    border-radius: 4px;

    .device-item {
      display: flex;
      align-items: center;
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }

      .device-id {
        margin-left: 8px;
        color: #666;
      }

      .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #52c41a;
        margin-left: auto;
      }
    }
  }

  .steps-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 32px;
    .steps {
      width: 560px;
      height: 76px;
      background: rgba(66, 56, 201, 0.04);
      border: 1px dashed #e5e6eb;
      border-radius: 8px;
      position: relative;
      .step-wrapper-container {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        .step-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
        }
      }

      .step-number {
        font-size: 20px;
        font-weight: 600;
        color: #c9cdd4;
        font-family: DINAlternate-Bold, DINAlternate;
        position: absolute;
        left: 0;
        top: 0;
        z-index: 88;
        width: 32px;
        height: 32px;

        &.active {
          background: linear-gradient(90deg, #2b5adc 0%, #22b9e7 100%);
          -webkit-background-clip: text;
          color: transparent;
        }
      }
      .number-1 {
        left: 65px;
        top: -12px;
      }
      .number-2 {
        left: 323px;
        top: -12px;
      }

      .step-content {
        display: flex;
        align-items: center;
        color: #999;

        &.active {
          color: #333;
        }

        .step-icon {
          width: 32px;
          height: 32px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 8px;

          .anticon {
            color: #fff;
            font-size: 16px;
          }
        }

        .step-text {
          font-size: 14px;
        }
      }

      .step-arrow {
        margin: 0 40px;
        color: #d9d9d9;
        font-size: 16px;
      }
    }
  }

  .footer {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    padding-top: 12px;
    border-top: 1px solid #d5d9dd;
    padding-bottom: 12px;
    padding-right: 16px;
  }
`
